import './App.css'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import LazyImage from './LazyImage'

function EaglesBlog (): JSX.Element {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  const images = [
    'https://cdn.eqsong.com/eqsong-images/Maps-art.jpg',
    'https://cdn.eqsong.com/eqsong-images/A26I7700-enhance-1.7x-Sha.jpg',
    'https://cdn.eqsong.com/eqsong-images/Approximate-Focus-Distance-65m.jpg',
    'https://cdn.eqsong.com/eqsong-images/A26I7870-21-art-scale-2X.jpg'
  ]

  const nextImage = (): void => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length)
  }

  const prevImage = (): void => {
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length)
  }

  return (
    <div className="page blog-page">
      <nav className="page-nav">
        <Link to="/" className="btn btn-secondary">← Back to Home</Link>
      </nav>

      <section className="page-header">
        <h1>🦅 Conowingo Dam Eagles Photo Trip</h1>
        <p>An unforgettable eagle photography trip to Conowingo Dam, Maryland - November 13-16, 2023</p>
        <div className="blog-meta">
          <span>Published: November 2023</span>
          <span>Category: Photography Trip</span>
          <span>Location: Conowingo Dam, Maryland</span>
        </div>
      </section>

      <div className="blog-container">
        <article className="blog-article">
          <section className="blog-section">
            <h2>Introduction</h2>
            <p>
              The Conowingo Dam in Maryland, USA, is indeed a fascinating place, especially for bird enthusiasts and photographers. 
              The hydroelectric generators of the dam create a unique ecosystem where fish that get too close to the generators' intake 
              are sucked in and spit out the other side, either chopped into bits or stunned. This phenomenon attracts a large number of 
              bald eagles, especially during the winter months, creating a spectacular sight.
            </p>
          </section>

          <section className="blog-section">
            <h2>A Gathering of Eagles</h2>
            <p>
              The bald eagles at Conowingo Dam are a sight to behold. Dozens of these majestic birds nest and live permanently in 
              Susquehanna State Park near the dam. In winter, near 100 bald eagles have been reported to gather and feast on the 
              disoriented fish. However, the number of eagles has decreased since its likely peak time in the late 2010s. For example, 
              the counting number of eagles was often around 20-30s in November 2023, according to the popular Conowingo Bald Eagles 
              Facebook Page. This decrease could be due to pollution, habitat loss, human disturbance, and natural fluctuations.
            </p>
            <p>
              <a href="https://qing-mei.smugmug.com/Conowingo-Dam/" target="_blank" rel="noopener noreferrer">
                View high resolution Conowingo eagle images
              </a>
            </p>
          </section>

          <section className="blog-section">
            <h2>Photography Gallery</h2>
            <div className="image-carousel">
              <button className="carousel-btn prev" onClick={prevImage}>❮</button>
              <div className="carousel-container">
                <LazyImage src={images[currentImageIndex]} alt={`Eagle ${currentImageIndex + 1}`} className="blog-image carousel-image" />
                <div className="carousel-counter">{currentImageIndex + 1} / {images.length}</div>
              </div>
              <button className="carousel-btn next" onClick={nextImage}>❯</button>
            </div>
            <div className="carousel-indicators">
              {images.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(index)}
                />
              ))}
            </div>
          </section>

          <section className="blog-section">
            <h2>A Photographer's Paradise</h2>
            <p>
              The Conowingo Dam is a popular spot for photographers aiming to capture the beauty of these bald eagles. The best location 
              to see bald eagles at Conowingo Dam is at the public access area at Shures Landing Road. Eagles can be seen perched in the 
              woods along both river shorelines, even in the trees adjacent to the Conowingo Fisherman's Park car lot. It's a truly 
              remarkable sight and a testament to the resilience and majesty of these incredible birds.
            </p>
            <p>
              Getting spectacular photographs of bald eagles fishing can be tricky. Here are some interesting points from my very first 
              trip to the dam.
            </p>
          </section>

          <section className="blog-section">
            <h2>Photography Insights and Tips</h2>
            
            <h4>1. Generator Operation and Fish Behavior</h4>
            <p>
              The working condition of the generators at the Conowingo Dam seems to have a significant impact on the behavior of the fish 
              and, consequently, the bald eagles. When the two internal turbines (driving generators no. 2 and 6) are running, this is 
              considered a normal condition and occurs most of the time for the period of my visit.
            </p>
            <p>
              However, when too many turbines are running (sometimes with 6 more external turbines in the middle of the dam), it produces 
              a strong current that pushes the fish further away from the shoreline and even to the other side of the island. This could 
              potentially affect the feeding patterns of the bald eagles and might be one of the factors influencing their numbers at the 
              dam. It's fascinating to see how the operation of the dam intertwines with the natural ecosystem in such a profound way.
            </p>

            <h4>2. Wind and Blow Direction</h4>
            <p>
              The wind and blow direction indeed play a crucial role in the behavior of the eagles and the opportunities for photographers 
              at the Conowingo Dam. Eagles use a moderated headwind to lift fish, and strong or gusty winds may not be favorable for their 
              fishing action.
            </p>
            <p>
              Predicting the headwind direction near the dam can be challenging for photographers due to the variable local wind directions, 
              which can be very different from areas further away from the dam. However, generally speaking, a south wind direction is 
              preferred for capturing the eagle fishing action at the park.
            </p>

            <h4>3. Finding the Right Spot and Timing</h4>
            <p>
              Finding the right spot and timing is crucial for capturing the best shots of the bald eagles at the Conowingo Dam.
            </p>
            <p>
              <strong>Good Spot:</strong> The best close-distance action typically happens near the so-called fishing pier and fence line. 
              These areas provide the best chances of witnessing eagle-fishing and related shows. The close distance could be around or less 
              than 100 yards from the shoreline.
            </p>
            <p>
              <strong>Timing:</strong> Positioning yourself in the afternoon sun between 12pm and 3pm can offer the best light and action, 
              especially in November or December. As the golden rule of bird photography goes, "distance is the king." So, being as close 
              as safely possible can significantly enhance the quality of the photographs.
            </p>

            <h4>4. Camera and Lens Selection</h4>
            <p>
              Choosing the right camera and lens is important for capturing the best shots of the bald eagles at the Conowingo Dam.
            </p>
            <p>
              <strong>Camera:</strong> A mirrorless or DSLR camera from a reputable brand is recommended. These cameras typically offer 
              superior image quality, performance, and flexibility compared to compact cameras or smartphones.
            </p>
            <p>
              <strong>Lens:</strong> A telephoto lens with a focal length of 400mm or more is recommended. In my experience, a telephoto 
              lens with 600mm x 1.4 TC seems to be the best choice. This type of lens allows photographers to capture close-up shots of 
              the eagles from a distance.
            </p>
            <p>
              However, there's a catch. Eagle-fishing action may occasionally happen at a very close range, something like 10 yards in 
              front of the fence line, as reported on Dec. 8, 2023, on the Conowingo Bald Eagles Facebook page. In such cases, a 
              relatively shorter zoom lens would be a much better choice.
            </p>

            <h4>5. Observing Eagle Behavior</h4>
            <p>
              Observing the eagle-fishing action at the Conowingo Dam can be a thrilling experience.
            </p>
            <p>
              <strong>Eagle Behavior:</strong> There are two or more pairs of experienced local eagles who often offer the best shows. 
              These experienced eagles can suddenly dive down from trees behind the parking lots unexpectedly to catch fish in the areas 
              close to the shoreline. They will chase each other, with more juveniles joining in for subsequently spectacular actions! 
              Juveniles, on the other hand, prefer to dive from towers at the island to the right side of the fence line in close distance.
            </p>
            <p>
              <strong>Patience and Practice:</strong> Patience and practice tracking the eagles in flight are key. Eagles are wild animals, 
              and their actions are unpredictable. Being well-prepared and patient can help you witness these magnificent creatures in 
              action.
            </p>
            <p>
              <strong>Photography Tips:</strong> Practicing tracking the eagles in flight will help you get clear, focused high-quality 
              images at a relatively close distance. Remember, as the golden rule of bird photography goes, "distance is the king."
            </p>
          </section>

          <section className="blog-section">
            <h2>A Community of Eagle Lovers</h2>
            <p>
              The Conowingo Bald Eagles Facebook page is a community where bald eagle lovers, photographers, and visitors can view and 
              share information. It's a great place to connect with others who share a passion for these magnificent birds.
            </p>
            <p>
              The Conowingo Dam and its bald eagles offer a unique opportunity to witness nature's grandeur. Whether you're a photographer, 
              a birdwatcher, or just someone who appreciates wildlife, a visit to Conowingo Dam is sure to be an unforgettable experience.
            </p>
            <p>
              You can view more images of my trip at <a href="https://qing-mei.smugmug.com/Conowingo-Dam/" target="_blank" rel="noopener noreferrer">
                high resolution Conowingo eagle images
              </a>. Thank you for sharing these valuable insights and tips. They will undoubtedly be beneficial for bird watchers and 
              photographers planning to visit the dam. 😊
            </p>
          </section>

          <div className="related-content">
            <h3>Related Content</h3>
            <div className="related-blogs">
              <Link to="/blog/birding" className="related-blog-card">
                <span>🦅 Red-headed Woodpecker</span>
              </Link>
              <Link to="/blog/ai" className="related-blog-card">
                <span>📝 AI Fundamentals - Part I</span>
              </Link>
              <Link to="/gallery" className="related-blog-card">
                <span>📸 Photo Gallery</span>
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>
  )
}

export default EaglesBlog
